import { createContext, useContext, useEffect, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { FollowupFieldViewDecisionEnum, type ImeSeriousnessPreview } from "@/lib/api/generated/casedb";
import { followupBadgeKey, type FollowupReviewSummary } from "@/features/case_follow_up/review/types/followupReviewView";
import type { CaseReviewCollectionApi } from "../../shared/model/caseReviewDrafts";
import { imeSeriousnessFor } from "../services/imeSeriousnessService";
import { IME_SERIOUSNESS_QUERY_KEY, IME_SERIOUSNESS_TEXT } from "../constants/imeSeriousnessLabels";
import {
  IME_SERIOUSNESS_KEYS, imeFieldChanges, imeTargetKey, imeTargetOf, imeTermLabel,
  type ImeFieldChange, type ImeTarget,
} from "../model/imeSeriousness";

/** What the note under one event's switch says. */
export interface ImeRowStatus {
  kind: "checking" | "updated" | "kept" | "failed";
  message: string;
  /** Asks the list again after a failed read. */
  retry?: () => void;
}

/** The EMA IME list of every event of the case being edited (product owner, 25 Oct 2026). */
export interface ImeSeriousnessSync {
  /** A changed PT is being checked, or its check failed: Save waits until it ends or the switch is off. */
  pending: boolean;
  statusOf(recordId: string): ImeRowStatus | undefined;
  /** The reviewer accepted the confirmation: apply what the list gives, for the PT it was asked for. */
  accept(recordId: string, target: ImeTarget, preview: ImeSeriousnessPreview): void;
  /** The reviewer turned the switch off: no list applies to the event any more. */
  forget(recordId: string): void;
}

export interface ImeSeriousnessSyncInput {
  editing: boolean;
  revision: number | undefined;
  /** The case's events as the review drafts hold them; absent while the review loads. */
  events: CaseReviewCollectionApi | undefined;
  /** The case's follow-up proposals; a field that one of them decides keeps its value. Absent without follow-ups. */
  followups: FollowupReviewSummary | undefined;
}

/**
 * Whether a follow-up proposal decides the field in this save: it waits for the reviewer, or the reviewer decided it
 * in this session. The save refuses a field written both by a follow-up decision and by the ordinary drafts.
 */
function followupDecides(followups: FollowupReviewSummary | undefined, fieldId: string, recordId: string): boolean {
  return (followups?.badgeIndex.get(followupBadgeKey(fieldId, recordId)) ?? [])
    .some((proposal) => proposal.decision === FollowupFieldViewDecisionEnum.Pending || proposal.draft !== undefined);
}

/** The label that the catalog gives an event field. */
function labelOf(events: CaseReviewCollectionApi, key: string): string {
  const property = (events.schema?.properties as Record<string, { title?: string }> | undefined)?.[key];
  return property?.title ?? key;
}

/** One event whose PT changed while its switch is on, and the PT the list has to judge. */
interface Need { recordId: string; target: ImeTarget; key: string }

/** Per event: the PT key the seriousness matches, and the note under the switch. */
interface Store { applied: Record<string, string>; statuses: Record<string, ImeRowStatus> }
const EMPTY_STORE: Store = { applied: {}, statuses: {} };

export const ImeSeriousnessSyncContext = createContext<ImeSeriousnessSync | undefined>(undefined);
export const useImeSeriousnessSyncContext = () => useContext(ImeSeriousnessSyncContext);

/** The note after the list applied to a changed PT. */
function updatedStatus(preview: ImeSeriousnessPreview, kept: string[]): ImeRowStatus {
  const updated = !preview.applies ? IME_SERIOUSNESS_TEXT.keptNoPreferredTerm
    : preview.on_list ? IME_SERIOUSNESS_TEXT.updatedOnList(imeTermLabel(preview))
      : IME_SERIOUSNESS_TEXT.updatedNotOnList(imeTermLabel(preview));
  return { kind: "updated", message: updated + (kept.length ? IME_SERIOUSNESS_TEXT.followupOwned(kept.join(" and ")) : "") };
}

/**
 * "Derive seriousness from the EMA IME list" across the events of the case being edited.
 *
 * <p>While an event's switch is on, every change of its preferred term (PT) applies the list to the new PT on the
 * screen: typed, coded from the dictionary, proposed by Theragenie, or reverted. A PT on the list gives only Medically
 * Significant and the system sentence; a PT that is not on it, no criterion and empty notes; no PT, nothing. The values
 * change as reviewer edits, so the save stores them as sent. A field that a follow-up proposal decides keeps its value.
 * It watches every event, not only the one on screen: the PT is coded on another tab than the switch.
 *
 * <p>Effects: the list answers asynchronously, and its answer must reach the drafts without a click.
 */
export function useImeSeriousnessSync(input: ImeSeriousnessSyncInput): ImeSeriousnessSync {
  const { editing, revision, events, followups } = input;
  const [store, setStore] = useState<Store>(EMPTY_STORE);
  // Every edit session and every new revision start from the saved values.
  useEffect(() => { setStore(EMPTY_STORE); }, [editing, revision]);

  const needs: Need[] = [];
  const noTerm: Record<string, string> = {};
  for (const row of editing && events ? events.rows : []) {
    if (row.values[IME_SERIOUSNESS_KEYS.switch] !== true) continue;
    const target = imeTargetOf(row.values);
    const key = imeTargetKey(target);
    if (key === (store.applied[row.recordId] ?? imeTargetKey(imeTargetOf(row.saved)))) continue;
    if (target.kind === "none") noTerm[row.recordId] = key;
    else needs.push({ recordId: row.recordId, target, key });
  }
  const results = useQueries({
    queries: needs.map((need) => ({
      queryKey: [IME_SERIOUSNESS_QUERY_KEY, need.key],
      queryFn: () => imeSeriousnessFor(need.target),
      retry: false,
      staleTime: 10 * 60_000,
    })),
  });

  /** Makes the event's fields what the list gives, as reviewer edits, except the fields a follow-up decides. */
  const apply = (recordId: string, preview: ImeSeriousnessPreview): string[] => {
    const row = events?.rows.find((candidate) => candidate.recordId === recordId);
    if (!events || !row) return [];
    const kept: string[] = [];
    imeFieldChanges(preview, row.values, row.saved).forEach((change: ImeFieldChange) => {
      if (followupDecides(followups, `${events.prefix}${change.key}`, recordId)) { kept.push(labelOf(events, change.key)); return; }
      if (change.action === "restore") { events.undo(recordId, change.key); return; }
      events.edit(recordId, change.key);
      events.changeField(recordId, change.key, change.value);
    });
    return kept;
  };

  // Applies each answer once: after it, the event's PT key is the applied one and the event needs nothing.
  const ready = needs.map((need, index) => (results[index]?.data ? `${need.recordId}=${need.key}` : "")).join("|")
    + "#" + Object.entries(noTerm).map(([recordId, key]) => `${recordId}=${key}`).join("|");
  useEffect(() => {
    const applied: Record<string, string> = {};
    const statuses: Record<string, ImeRowStatus> = {};
    needs.forEach((need, index) => {
      const preview = results[index]?.data;
      if (!preview) return;
      statuses[need.recordId] = updatedStatus(preview, apply(need.recordId, preview));
      applied[need.recordId] = need.key;
    });
    for (const [recordId, key] of Object.entries(noTerm)) {
      applied[recordId] = key;
      statuses[recordId] = { kind: "kept", message: IME_SERIOUSNESS_TEXT.keptNoPreferredTerm };
    }
    if (Object.keys(applied).length === 0) return;
    setStore((current) => ({ applied: { ...current.applied, ...applied }, statuses: { ...current.statuses, ...statuses } }));
  }, [ready]);

  return {
    pending: needs.some((_, index) => !results[index]?.data),
    statusOf: (recordId) => {
      const index = needs.findIndex((need) => need.recordId === recordId);
      if (index < 0) return store.statuses[recordId];
      const result = results[index];
      const term = needs[index].target.kind === "term" ? (needs[index].target as { preferredTerm: string }).preferredTerm : undefined;
      if (result?.isError) {
        return { kind: "failed", message: IME_SERIOUSNESS_TEXT.checkFailed(term), retry: () => void result.refetch() };
      }
      return { kind: "checking", message: IME_SERIOUSNESS_TEXT.checking(term) };
    },
    accept: (recordId, target, preview) => {
      const kept = apply(recordId, preview);
      setStore((current) => {
        const statuses = { ...current.statuses };
        if (kept.length) statuses[recordId] = updatedStatus(preview, kept);
        else delete statuses[recordId];
        return { applied: { ...current.applied, [recordId]: imeTargetKey(target) }, statuses };
      });
    },
    forget: (recordId) => setStore((current) => {
      const applied = { ...current.applied };
      const statuses = { ...current.statuses };
      delete applied[recordId];
      delete statuses[recordId];
      return { applied, statuses };
    }),
  };
}
